import type { ReactNode } from 'react';
import { Box, HStack, Icon as ChakraIcon, Text, VStack, type BoxProps } from '@chakra-ui/react';
import { Lock, Crown, type LucideIcon } from 'lucide-react';
import { UpgradeCtaButton, useUpgradeAction, type UpgradeActionOptions } from './UpgradeCtaButton';
import { FOCUS_RING_SX, INTERACTIVE_TRANSITION_SX, PRESS_FEEDBACK } from './lockedFeatureStyles';

/**
 * D51f — the shared locked-with-CTA component. Four variants cover the five
 * migrated premium locks (formerly hand-rolled) plus the Digest archive
 * surfaces:
 *
 * - **badge** — a small "Paire+" pill overlaid on a still-visible, still-
 *   tappable option tile (`VotingModeSelector.tsx`'s voting-mode tiles).
 * - **row** — a tappable list row whose content stays fully legible, with a
 *   trailing lock glyph as the only visual difference from an unlocked row
 *   (Previous Issues strip, All Issues archive rows).
 * - **banner** — icon + headline + body + CTA on a tinted surface, inside a
 *   modal or inline block (`CreateListModal.tsx`,
 *   `CreateCollaborativeListModal.tsx`, `MemberManagementSection.tsx`).
 * - **card** — a standalone feature-gated panel, same shape as `banner` but
 *   centered by default and not "you hit a limit" framed
 *   (`TasteInsightsSnippet.tsx`, the past-issue 403 state).
 *
 * See `docs/current_work/planning/d51f_design_notes.md` §1 for the design
 * rationale, including the Crown-vs-Lock convention this component enforces
 * (`Crown` = "here's what Paire+ buys", `Lock` = "this specific thing is
 * locked").
 *
 * ## Tiles (badge, row) always render their own shell
 *
 * A tile is ALWAYS a native `<button>`, locked or not, so an unlocked and a
 * locked instance look and behave identically apart from the glyph/badge —
 * and so a caller never needs a conditional wrapper. `children` are the
 * tile's visible content and MUST be non-interactive (no buttons, links or
 * inputs): they sit inside a `<button>`, and interactive content inside a
 * button is invalid HTML and unreachable by keyboard.
 *
 * - **locked** — the shell is labelled `"${friendlyName}, locked — opens
 *   Paire+ options"` (this REPLACES the children's text as the accessible
 *   name, so `friendlyName` must include the tile's teaser content, e.g.
 *   "Survivor mode: eliminate restaurants round by round"), activating it
 *   runs the upgrade action, and the glyph/badge is shown.
 * - **unlocked** — no `aria-label` (the children name the button), activating
 *   it calls `onSelect`, and there is no glyph.
 *
 * Tile chrome (spacing, background, border, hover…) comes from `shellProps`.
 * Precedence: LockedFeature's defaults < `shellProps` < what LockedFeature
 * owns. The owned set — the element, its click handler, `aria-label`,
 * `transition` (with its reduced-motion counterpart), and the focus ring —
 * is omitted from the `shellProps` type, as are `opacity`, `filter`,
 * `backdropFilter` and `position` (they would dim or displace the pill).
 * `borderRadius`, `_hover` and `_active` are defaults the caller may override.
 *
 * Session-replay masking. `row`, `banner` and `card` carry `ph-no-capture`.
 * The `badge` variant does NOT: its shell takes no `className` (it is owned by
 * `LockedFeature`, see `LockedShellProps`), and masking it would also stop
 * PostHog autocapturing taps on the voting-mode tiles it wraps, an analytics
 * change that is not this component's to make. A badge tile is therefore
 * UNMASKED: its `children` (and `friendlyName`) must carry only fixed, non-
 * member-specific copy (mode names and descriptions), never member data.
 *
 * Rows are verified against `--card-bg` hosts only (their hover surfaces are
 * checked against that background); on any other host, pass your own `_hover`.
 *
 * A swipe/carousel path that opens the upgrade modal (VotingModeSelector's
 * Swiper `onSlideChange`) stays in the caller.
 *
 * ## Banner and card
 *
 * Painted over an opaque `--card-bg` (the purple tint is a `backgroundImage`
 * on top), so `--card-text`/`--card-text-2` are valid on them everywhere —
 * including directly on the always-dark Profile canvas, where this makes the
 * component a white card in light mode. The container carries no `aria-label`
 * and no `role`; the visible CTA text is its accessible name. Pass
 * `showCta={false}` when the host renders its own action (modal footers use
 * the exported `UpgradeCtaButton` next to "Maybe Later").
 */

export type LockedFeatureVariant = 'badge' | 'banner' | 'card' | 'row';

/**
 * Chrome a caller may supply for a tile's shell. Omitted: the pieces
 * `LockedFeature` owns (see the component doc) — so the shell stays a native
 * `<button>` with one focus ring, one transition and one accessible-name rule.
 */
export type LockedShellProps = Omit<
  BoxProps,
  | 'as'
  | 'type'
  | 'role'
  | 'tabIndex'
  | 'onClick'
  | 'onKeyDown'
  | 'aria-label'
  | 'children'
  | 'className'
  | 'data-testid'
  | 'transition'
  | 'sx'
  | '_focusVisible'
  // A dimmed/filtered/repositioned shell would dim or displace the "Paire+"
  // pill child, which must always read at full contrast (`useTileOwnedProps`
  // enforces the first three at runtime too).
  | 'opacity'
  | 'filter'
  | 'backdropFilter'
  | 'position'
>;

interface LockedFeatureBaseProps extends UpgradeActionOptions {
  /**
   * Whether this instance is currently gating. For `banner`/`card`, `false`
   * renders nothing (they have no unlocked content of their own). For
   * `badge`/`row`, `false` renders the same shell without the glyph/badge or
   * the compound label, calling `onSelect` on activation.
   */
  locked: boolean;
  /** Stable test/automation hook, forwarded to the outermost rendered element. */
  testId?: string;
}

interface LockedTileProps extends LockedFeatureBaseProps {
  /**
   * Human-readable subject for the locked accessible name. Because the
   * compound `aria-label` replaces the children's text, include the tile's
   * teaser content, not just its title.
   */
  friendlyName: string;
  /** The tile's visible content. Must be non-interactive — it sits inside a `<button>`. */
  children: ReactNode;
  /** Fired on activation when `locked === false`. */
  onSelect?: () => void;
  /** Tile chrome; see the component doc for precedence. */
  shellProps?: LockedShellProps;
  /**
   * Overrides the tile's Lock glyph. Use sparingly — see the Crown-vs-Lock
   * convention above.
   */
  icon?: LucideIcon;
}

interface LockedFeatureBadgeProps extends LockedTileProps {
  variant: 'badge';
}

interface LockedFeatureRowProps extends LockedTileProps {
  variant: 'row';
}

interface LockedPanelProps extends LockedFeatureBaseProps {
  headline?: string;
  /** Supporting copy. With no `headline`, it is the panel's one message and renders as primary text. */
  body?: string;
  /** Defaults to "Upgrade to Paire+". */
  ctaLabel?: string;
  /** Render the built-in CTA. Defaults to `true`. */
  showCta?: boolean;
  /**
   * `start`: icon beside the text, CTA left-aligned. `center`: stacked and
   * centered. Banner defaults to `start`, card to `center`. A single static
   * value: for responsive alignment, resolve it in the caller (e.g.
   * `useBreakpointValue`, as `VotingModeSelector.tsx` does) and pass the result.
   */
  align?: 'start' | 'center';
  /** CTA size (and icon size). `sm` is still a 44px touch target. Defaults to `md`. */
  size?: 'sm' | 'md';
  /**
   * Overrides the panel's own Crown glyph ONLY — the CTA's glyph is always
   * Crown. Use sparingly — see the Crown-vs-Lock convention above.
   */
  icon?: LucideIcon;
}

interface LockedFeatureBannerProps extends LockedPanelProps {
  variant: 'banner';
}

interface LockedFeatureCardProps extends LockedPanelProps {
  variant: 'card';
  /** Rendered under the CTA (e.g. a "Back to your latest issue" action). */
  secondaryAction?: ReactNode;
  /** Render the headline as `<h{n}>` instead of a plain paragraph. Omit for a non-heading headline. */
  headingLevel?: 2 | 3 | 4 | 5 | 6;
}

export type LockedFeatureProps =
  | LockedFeatureBadgeProps
  | LockedFeatureRowProps
  | LockedFeatureBannerProps
  | LockedFeatureCardProps;

export function LockedFeature(props: LockedFeatureProps) {
  switch (props.variant) {
    case 'badge':
      return <LockedBadge {...props} />;
    case 'row':
      return <LockedRow {...props} />;
    case 'banner':
    case 'card':
      return props.locked ? <LockedPanel {...props} /> : null;
  }
}

// --- tiles ------------------------------------------------------------------

/** `${friendlyName}, locked — opens Paire+ options`: "opens", not "tap", so it is true for keyboard and pointer alike. */
function lockedAriaLabel(friendlyName: string): string {
  return `${friendlyName}, locked — opens Paire+ options`;
}

/**
 * Everything LockedFeature owns on a tile shell, common to badge and row —
 * spread AFTER a caller's `shellProps` so none of it can be overridden.
 * `transition` is fixed here (never a caller prop) so the reduced-motion
 * counterpart in `sx` can't be bypassed.
 */
function useTileOwnedProps({
  locked,
  friendlyName,
  onSelect,
  testId,
  context,
  onUpgradeClick,
  onBeforeUpgrade,
}: LockedTileProps) {
  const onUpgrade = useUpgradeAction({ context, onUpgradeClick, onBeforeUpgrade });
  return {
    as: 'button' as const,
    type: 'button' as const,
    onClick: locked ? onUpgrade : onSelect,
    'aria-label': locked ? lockedAriaLabel(friendlyName) : undefined,
    'data-testid': testId,
    sx: INTERACTIVE_TRANSITION_SX,
    ...FOCUS_RING_SX,
    // Never dim/filter the shell (it would dim the pill child) — even if a
    // caller bypasses the `LockedShellProps` type with a cast.
    opacity: undefined,
    filter: undefined,
    backdropFilter: undefined,
  };
}

function LockedBadge(props: LockedTileProps) {
  const { locked, icon, children, shellProps } = props;
  const owned = useTileOwnedProps(props);
  const IconGlyph = icon ?? Lock;
  return (
    <Box
      width="100%"
      textAlign="left"
      minH="44px"
      borderRadius="var(--r-card-compact)"
      _active={PRESS_FEEDBACK}
      {...shellProps}
      // Position anchors the pill; a caller can't opt out of that.
      position="relative"
      {...owned}
    >
      {children}
      {locked && (
        <Box
          aria-hidden="true"
          position="absolute"
          top={2}
          right={2}
          display="flex"
          alignItems="center"
          gap={1}
          borderRadius="full"
          px={2}
          py={0.5}
          bg="var(--paire-plus-pill-bg)"
          color="var(--paire-plus-pill-text)"
        >
          <IconGlyph size={12} />
          {/* "Paire+" is the brand's own casing — deliberately not uppercased. */}
          <Text as="span" fontSize="xs" fontWeight="bold" lineHeight="1">
            Paire+
          </Text>
        </Box>
      )}
    </Box>
  );
}

function LockedRow(props: LockedTileProps) {
  const { locked, icon, children, shellProps } = props;
  const owned = useTileOwnedProps(props);
  const IconGlyph = icon ?? Lock;
  return (
    <Box
      width="100%"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      textAlign="left"
      minH="44px"
      borderRadius="var(--r-card-compact)"
      // Locked: the same purple tint the panels wear. Unlocked: a neutral
      // surface — the tint means "locked", so it must not appear on an open
      // row. Both are `background-color`s so `INTERACTIVE_TRANSITION_SX` fades
      // them (a gradient `background-image` cannot be transitioned).
      _hover={{ bg: locked ? 'var(--locked-surface-hover)' : 'var(--card-surface-2)' }}
      _active={PRESS_FEEDBACK}
      {...shellProps}
      {...owned}
      className="ph-no-capture"
    >
      <Box flex="1" minWidth={0}>
        {children}
      </Box>
      {locked && (
        <IconGlyph size={14} aria-hidden="true" color="var(--locked-icon-accent)" style={{ flexShrink: 0 }} />
      )}
    </Box>
  );
}

// --- banner / card --------------------------------------------------------

/** Banner and card share one renderer; the card-only props are optional here, derived (not re-declared) from the public types. */
type PanelInternalProps = (LockedFeatureBannerProps | LockedFeatureCardProps) &
  Partial<Pick<LockedFeatureCardProps, 'secondaryAction' | 'headingLevel'>>;

function LockedPanel(props: PanelInternalProps) {
  const {
    variant,
    headline,
    body,
    ctaLabel,
    showCta = true,
    size = 'md',
    icon,
    testId,
    context,
    onUpgradeClick,
    onBeforeUpgrade,
    secondaryAction,
    headingLevel,
  } = props;
  const align = props.align ?? (variant === 'card' ? 'center' : 'start');
  const centered = align === 'center';
  const IconGlyph = icon ?? Crown;
  const iconSize = size === 'sm' ? 5 : 10;

  const glyph = <ChakraIcon as={IconGlyph} boxSize={iconSize} color="var(--locked-icon-accent)" aria-hidden="true" flexShrink={0} />;
  const textStack = (
    <VStack align={centered ? 'center' : 'start'} spacing={1} flex={1} minW={0}>
      {headline && (
        <Text
          as={headingLevel ? (`h${headingLevel}` as const) : undefined}
          fontWeight="bold"
          textAlign={centered ? 'center' : 'left'}
          color="var(--card-text)"
        >
          {headline}
        </Text>
      )}
      {body && (
        // With no headline the body IS the message, so it takes the primary
        // text token at body size rather than reading as secondary copy.
        <Text
          fontSize={headline ? 'sm' : 'md'}
          textAlign={centered ? 'center' : 'left'}
          color={headline ? 'var(--card-text-2)' : 'var(--card-text)'}
        >
          {body}
        </Text>
      )}
    </VStack>
  );

  return (
    // Opaque base + tint as an image: the card text tokens are always valid here.
    <Box
      backgroundColor="var(--card-bg)"
      backgroundImage="var(--locked-surface-bg)"
      borderRadius="var(--r-card-compact)"
      borderWidth="1px"
      borderColor="var(--locked-surface-border)"
      p={4}
      data-testid={testId}
      className="ph-no-capture"
    >
      <VStack align={centered ? 'center' : 'stretch'} spacing={3}>
        {centered ? (
          <>
            {glyph}
            {textStack}
          </>
        ) : (
          <HStack spacing={3} align="start">
            {glyph}
            {textStack}
          </HStack>
        )}
        {showCta && (
          <UpgradeCtaButton
            context={context}
            onUpgradeClick={onUpgradeClick}
            onBeforeUpgrade={onBeforeUpgrade}
            label={ctaLabel}
            size={size}
            layout={centered ? undefined : { alignSelf: 'start' }}
          />
        )}
        {secondaryAction}
      </VStack>
    </Box>
  );
}
